import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, Loader2 } from 'lucide-react';
import { COMPLETION_COPY, ONBOARDING_COMPLETION_ITEM_STAGGER_MS } from '../constants';

const springPop = { type: 'spring' as const, stiffness: 500, damping: 22 };
const springSoft = { type: 'spring' as const, stiffness: 320, damping: 26 };

function ItemStatusIcon({ isComplete, isActive }: { isComplete: boolean; isActive: boolean }) {
  return (
    <span
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
        isComplete
          ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/30'
          : isActive
            ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500/40'
            : 'bg-gray-100'
      }`}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isComplete ? (
          <motion.span
            key="check"
            initial={{ scale: 0, rotate: -120 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0 }}
            transition={springPop}
          >
            <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
          </motion.span>
        ) : isActive ? (
          <motion.span
            key="loader"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.2 }}
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          </motion.span>
        ) : (
          <motion.span
            key="pending"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="h-2 w-2 rounded-full bg-gray-300"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>
    </span>
  );
}

export default function OnboardingCompletion() {
  const [visibleCount, setVisibleCount] = useState(0);
  const total = COMPLETION_COPY.items.length;
  const allComplete = visibleCount >= total;
  const progress = visibleCount / total;

  useEffect(() => {
    const timers = COMPLETION_COPY.items.map((_, i) =>
      setTimeout(() => setVisibleCount(i + 1), (i + 1) * ONBOARDING_COMPLETION_ITEM_STAGGER_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col items-center justify-center px-6 py-8 text-center sm:px-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={springSoft}
        className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-4 ring-emerald-100"
      >
        <AnimatePresence mode="wait">
          {allComplete ? (
            <motion.div
              key="done"
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={springPop}
            >
              <Check className="h-7 w-7 text-emerald-600" strokeWidth={2.5} aria-hidden="true" />
            </motion.div>
          ) : (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <Loader2 className="h-7 w-7 animate-spin text-emerald-600" aria-hidden="true" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springSoft, delay: 0.05 }}
        className="mb-6 text-lg font-semibold tracking-tight text-gray-900 sm:text-xl"
      >
        {COMPLETION_COPY.title}
      </motion.p>

      <div className="mb-6 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-gray-100">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600"
          initial={{ width: '0%' }}
          animate={{ width: `${Math.max(progress * 100, 8)}%` }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        />
      </div>

      <ul className="w-full max-w-xs space-y-2 text-left">
        {COMPLETION_COPY.items.map((item, i) => {
          const isComplete = i < visibleCount;
          const isActive = i === visibleCount && !allComplete;
          const isPending = i > visibleCount;

          return (
            <motion.li
              key={item}
              layout
              initial={{ opacity: 0, x: -16 }}
              animate={{
                opacity: isPending ? 0.5 : 1,
                x: 0,
                scale: isActive ? 1.01 : 1,
              }}
              transition={springSoft}
              className={`flex items-center gap-3 rounded-xl px-2.5 py-2 transition-colors duration-300 ${
                isActive ? 'bg-emerald-50 shadow-sm ring-1 ring-emerald-200/80' : ''
              }`}
            >
              <ItemStatusIcon isComplete={isComplete} isActive={isActive} />
              <span
                className={`text-sm leading-snug transition-colors duration-300 ${
                  isComplete
                    ? 'font-medium text-gray-900'
                    : isActive
                      ? 'font-semibold text-gray-900'
                      : 'text-gray-400'
                }`}
              >
                {item}
              </span>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
